/**
 * The master appearance switch: one small icon button in the top bar of
 * every mode (Velora's top bar, the familiar modes' menu bar, Eaglesoft's
 * maximised activity windows and pop-out windows). One click opens
 * Velora / Dentrix / Open Dental / Eaglesoft; picking one switches in place
 * (no reload, no navigation: same page, same patient) and is saved per user
 * exactly like Settings > Appearance (`prefs.set({ skin })`, server-synced).
 *
 * Mod+Shift+L and the palette's "Switch appearance" open the visible switch.
 */
import { useNavigate } from '@tanstack/react-router';
import { SKIN_LABELS, type SkinId } from '@velora/contracts/skins';
import { Menu, type MenuEntry } from '@velora/ui';
import { Check, PanelsTopLeft, Settings2 } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { registerAction } from '../actions/registry.js';
import { registerCommand } from '../commands/registry.js';
import { usePrefs } from '../prefs/prefs.js';
import { shortcutLabels } from '../shortcuts/keys.js';
import { isMacPlatform } from '../shortcuts/platform.js';
import { registerShortcut } from '../shortcuts/registry.js';
import { useTerms } from '../skins/SkinProvider.js';
import { useCanOpenPath } from './useCanOpenPath.js';

/** Shortcut that opens the switch (shown in its tooltip and the `?` sheet). */
export const APPEARANCE_SWITCH_KEYS = 'mod+shift+l';

/** Menu order and the short names people say ("I know Dentrix"). */
export const SWITCH_ORDER: readonly SkinId[] = ['velora', 'dentrix', 'opendental', 'eaglesoft'];
export const SWITCH_NAMES: Record<SkinId, string> = {
  velora: 'Velora',
  dentrix: 'Dentrix',
  opendental: 'Open Dental',
  eaglesoft: 'Eaglesoft',
};

/* Every mounted switch, newest last: the shortcut opens the newest visible one. */
const mounted: Array<{ id: string; el: () => HTMLElement | null; open: () => void }> = [];

/** Opens the appearance menu of the switch on screen; false when none is visible. */
export function openAppearanceSwitch(): boolean {
  for (let i = mounted.length - 1; i >= 0; i -= 1) {
    const m = mounted[i];
    const el = m?.el();
    if (m && el && el.getClientRects().length > 0) {
      m.open();
      return true;
    }
  }
  return false;
}

/** Saves `skin` as this person's mode (same write as Settings > Appearance). */
export function useChooseSkin(): (skin: SkinId) => void {
  const prefs = usePrefs();
  const latest = useRef(prefs);
  latest.current = prefs;
  return (skin: SkinId) => latest.current.set({ skin, skinOnboarded: true });
}

export function AppearanceSwitch({ className }: { className?: string }) {
  const prefs = usePrefs();
  const choose = useChooseSkin();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const id = useId();
  const current = prefs.effectiveSkin;
  const keys = shortcutLabels(APPEARANCE_SWITCH_KEYS, isMacPlatform()).join('+');

  useEffect(() => {
    const entry = { id, el: () => ref.current, open: () => setOpen(true) };
    mounted.push(entry);
    return () => {
      const i = mounted.indexOf(entry);
      if (i >= 0) mounted.splice(i, 1);
    };
  }, [id]);

  const items: MenuEntry[] = [
    { type: 'label', id: 'head', label: 'Appearance' },
    ...SWITCH_ORDER.map<MenuEntry>((skin) => ({
      id: `skin-${skin}`,
      label: SWITCH_NAMES[skin],
      icon: skin === current ? <Check size={14} aria-hidden /> : <span aria-hidden />,
      title: SKIN_LABELS[skin],
      onSelect: () => {
        if (skin !== current) choose(skin);
      },
    })),
    { type: 'separator', id: 'sep' },
    {
      id: 'more',
      label: 'Appearance settings...',
      icon: <Settings2 size={14} aria-hidden />,
      onSelect: () => void navigate({ to: '/settings/appearance' }),
    },
  ];

  return (
    <Menu
      open={open}
      onOpenChange={setOpen}
      align="end"
      aria-label="Appearance"
      trigger={
        <button
          ref={ref}
          type="button"
          className={className ? `shell-iconbtn ${className}` : 'shell-iconbtn'}
          aria-label={`Appearance: ${SWITCH_NAMES[current]}. Switch appearance`}
          title={`Switch appearance (${keys}). Now: ${SWITCH_NAMES[current]}`}
          data-appearance-switch={current}
        >
          <PanelsTopLeft size={16} aria-hidden />
        </button>
      }
      items={items}
    />
  );
}

/* ------------------------------------------------------------ bindings */

/** Global shortcut for the schedule in every mode (the jump search lists it too). */
export const SCHEDULE_KEYS = 'mod+shift+s';
const SCHEDULE_PATH = '/schedule';

/**
 * Registers, for the main window and pop-outs alike: Mod+Shift+L (open the
 * switch), Mod+Shift+S (the schedule), and the jump-search entries
 * "Switch appearance", "Switch appearance to <mode>" and "Schedule" (in the
 * mode's own word: OnSchedule, Appointment Book, Appts).
 */
export function QuickNavBindings() {
  const prefs = usePrefs();
  const choose = useChooseSkin();
  const navigate = useNavigate();
  const canOpen = useCanOpenPath();
  const t = useTerms();
  const live = useRef({ navigate, canOpen, choose });
  live.current = { navigate, canOpen, choose };
  const current = prefs.effectiveSkin;
  const scheduleWord = t('schedule');
  const canSchedule = canOpen(SCHEDULE_PATH);

  useEffect(() => {
    const L = () => live.current;
    const openSwitch = () => {
      if (!openAppearanceSwitch()) void L().navigate({ to: '/settings/appearance' });
    };
    const goSchedule = () => {
      if (L().canOpen(SCHEDULE_PATH)) void L().navigate({ to: SCHEDULE_PATH });
    };
    const offs = [
      registerShortcut({
        id: 'shell.appearance-switch',
        keys: APPEARANCE_SWITCH_KEYS,
        description: 'Switch appearance (Velora, Dentrix, Open Dental, Eaglesoft)',
        section: 'Global',
        allowInInputs: true,
        run: openSwitch,
      }),
      registerAction({
        id: 'shell.switchAppearance',
        label: 'Switch appearance...',
        icon: 'settings',
        run: openSwitch,
      }),
      registerCommand({
        id: 'shell.appearance.switch',
        title: 'Switch appearance',
        group: 'Preferences',
        keywords: [
          'appearance',
          'mode',
          'look',
          'interface',
          'skin',
          'familiar',
          'velora',
          'dentrix',
          'open dental',
          'eaglesoft',
        ],
        hint: `Now ${SWITCH_NAMES[current]}`,
        icon: 'settings',
        shortcut: APPEARANCE_SWITCH_KEYS,
        weight: 2,
        run: openSwitch,
      }),
      ...SWITCH_ORDER.filter((s) => s !== current).map((skin) =>
        registerCommand({
          id: `shell.appearance.${skin}`,
          title: `Switch appearance to ${SWITCH_NAMES[skin]}`,
          group: 'Preferences',
          keywords: ['appearance', 'mode', 'look', 'interface', SWITCH_NAMES[skin].toLowerCase()],
          icon: 'settings',
          run: () => L().choose(skin),
        }),
      ),
    ];
    if (canSchedule) {
      offs.push(
        registerShortcut({
          id: 'shell.schedule',
          keys: SCHEDULE_KEYS,
          description: `Go to the schedule (${scheduleWord})`,
          section: 'Global',
          allowInInputs: true,
          run: goSchedule,
        }),
        registerCommand({
          id: 'shell.schedule',
          title: 'Schedule',
          group: 'Navigate',
          keywords: [
            'schedule',
            'appointments',
            'appts',
            'appointment book',
            'onschedule',
            'calendar',
            scheduleWord.toLowerCase(),
          ],
          ...(scheduleWord !== 'Schedule' ? { hint: scheduleWord } : {}),
          icon: 'calendar-days',
          shortcut: SCHEDULE_KEYS,
          weight: 3,
          run: goSchedule,
        }),
      );
    }
    return () => offs.forEach((off) => off());
  }, [current, scheduleWord, canSchedule]);

  return null;
}
